'use client';

import { CheckIcon } from '@radix-ui/react-icons';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';
const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;
const SelectTrigger = ({
  className,
  children,
  variant = 'default',
  ...props
}: React.ComponentPropsWithRef<typeof SelectPrimitive.Trigger> & {
  variant?: 'default' | 'hero';
}) => <SelectPrimitive.Trigger className={cn('flex h-10 w-full cursor-default items-center justify-between rounded-lg border border-input px-3 py-2 text-sm lg:text-base text-gray-600 shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-hidden focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50', variant === 'hero' ? '' : 'bg-white hover:border-zinc-400 active:border-zinc-400', className)} {...props} data-sentry-element="unknown" data-sentry-component="SelectTrigger" data-sentry-source-file="select.tsx">
    <span className="block flex-1 whitespace-nowrap truncate overflow-hidden text-left">
      {children}
    </span>
    <SelectPrimitive.Icon asChild data-sentry-element="unknown" data-sentry-source-file="select.tsx">
      <ChevronDown className="shrink-0" size={16} data-sentry-element="ChevronDown" data-sentry-source-file="select.tsx" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>;
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
const SelectContent = ({
  className,
  children,
  variant = 'default',
  position = 'popper',
  ...props
}: React.ComponentPropsWithRef<typeof SelectPrimitive.Content> & {
  variant?: 'default' | 'hero';
}) => <SelectPrimitive.Portal data-sentry-element="unknown" data-sentry-component="SelectContent" data-sentry-source-file="select.tsx">
    <SelectPrimitive.Content className={cn('relative z-50 min-w-[8rem] max-h-[70vh] overflow-x-hidden overflow-y-scroll rounded-md border shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2', position === 'popper' && 'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1', variant === 'hero' ? 'bg-blue-999 text-white' : 'bg-popover text-popover-foreground', className)} position={position} {...props} data-sentry-element="unknown" data-sentry-source-file="select.tsx">
      <SelectPrimitive.Viewport className={cn('p-1', position === 'popper' && 'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]')} data-sentry-element="unknown" data-sentry-source-file="select.tsx">
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>;
SelectContent.displayName = SelectPrimitive.Content.displayName;
const SelectLabel = ({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof SelectPrimitive.Label>) => <SelectPrimitive.Label className={cn('px-2 py-1.5 text-sm font-semibold', className)} {...props} data-sentry-element="unknown" data-sentry-component="SelectLabel" data-sentry-source-file="select.tsx" />;
SelectLabel.displayName = SelectPrimitive.Label.displayName;
const SelectItem = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<typeof SelectPrimitive.Item>) => <SelectPrimitive.Item className={cn('relative flex w-full cursor-default select-none items-center rounded-xs py-1.5 pl-2 pr-8 text-sm outline-hidden focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50', className)} {...props} data-sentry-element="unknown" data-sentry-component="SelectItem" data-sentry-source-file="select.tsx">
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator data-sentry-element="unknown" data-sentry-source-file="select.tsx">
        <CheckIcon className="h-4 w-4 text-pl-blue-600" data-sentry-element="CheckIcon" data-sentry-source-file="select.tsx" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText data-sentry-element="unknown" data-sentry-source-file="select.tsx">{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>;
SelectItem.displayName = SelectPrimitive.Item.displayName;
const SelectSeparator = ({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof SelectPrimitive.Separator>) => <SelectPrimitive.Separator className={cn('-mx-1 my-1 h-px bg-muted', className)} {...props} data-sentry-element="unknown" data-sentry-component="SelectSeparator" data-sentry-source-file="select.tsx" />;
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;
export { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue };